/* eslint-disable react/no-array-index-key */
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalBody,
  ModalHeader,
  Label,
  Col,
  Row,
  Input,
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';
import { ReactComponent as Add } from 'assets/images/Add.svg';

import AppButton from 'components/App/Button';
import AppInputValidation from 'components/App/InputValidation';
import Loading from 'components/App/Loading';
import AppSelectValidation from 'components/App/SelectValidation';

import { addContactsInfo } from 'utils/validate';

import { LANGUAGE_LABEL } from 'constants/setting';
import { showSuccess } from 'utils';
import styles from './ModalAddContactInfo.module.scss';

const gender = [
  { value: 'MALE', label: 'Nam' },
  { value: 'FEMALE', label: 'Nữ' },
  { value: 'OTHER', label: 'Khác' },
];

function ModalAddContactInfo(props) {
  const {
    modalType,

    placeId,

    isOpen,

    isLoadingGetPlaceContactDetails,
    placeContactDetails,

    contactGroup,

    languageSettings,
    roleSettings,

    onToggleModal,
    onCloseModal,
    onOpenModalDeletePlaceContact,
    onAddSuccess,

    actionAddPlaceContact,
    actionEditPlaceContact,
    actionGetPlaceContacts,
  } = props;

  const roleOptions = React.useMemo(
    () =>
      roleSettings.map((role) => ({
        value: role.name,
        label: role.vi,
      })),
    [roleSettings],
  );

  const languageOptions = React.useMemo(
    () =>
      languageSettings.map((lang) => {
        let label;

        switch (lang.name) {
          case 'VI':
            label = LANGUAGE_LABEL.VI;
            break;
          case 'EN':
            label = LANGUAGE_LABEL.EN;
            break;
          case 'FR':
            label = LANGUAGE_LABEL.FR;
            break;
          case 'CN':
            label = LANGUAGE_LABEL.CN;
            break;
          case 'JP':
            label = LANGUAGE_LABEL.JP;
            break;
          case 'KR':
            label = LANGUAGE_LABEL.KR;
            break;
          default:
            label = lang.name;
        }

        return { value: lang.name, label };
      }),
    [languageSettings],
  );

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      name:
        modalType === 'EDIT' && placeContactDetails
          ? placeContactDetails.name
          : '',
      gender:
        modalType === 'EDIT' && placeContactDetails
          ? placeContactDetails.gender
          : 'MALE',
      mainLanguage:
        modalType === 'EDIT' && placeContactDetails
          ? placeContactDetails.mainLanguage
          : '',
      role:
        modalType === 'EDIT' && placeContactDetails
          ? placeContactDetails.role
          : '',
      email:
        modalType === 'EDIT' && placeContactDetails
          ? placeContactDetails.email
          : '',
      phoneNumbers:
        modalType === 'EDIT' &&
        placeContactDetails &&
        Array.isArray(placeContactDetails.phoneNumbers)
          ? placeContactDetails.phoneNumbers
          : [''],
    },

    validationSchema: Yup.object(addContactsInfo),

    onSubmit: (values) => {
      const submittedValues = {
        place: placeId,
        ...values,
        phoneNumbers: values.phoneNumbers
          .filter(Boolean)
          .map((phoneNumber) => (
            phoneNumber.startsWith('0')
              ? `+84${phoneNumber.slice(1)}`
              : phoneNumber
          )),
        contactGroup,
      };

      const callBack = (createdContact) => {
        showSuccess(
          `${modalType === 'ADD' ? 'Tạo' : 'Chỉnh sửa'} liên hệ ${modalType === 'ADD' ? 'mới' : ''} thành công`,
        );
        actionGetPlaceContacts({ place: placeId });
        onCloseModal();
        if (modalType === 'ADD') {
          onAddSuccess(createdContact);
        }
        validation.resetForm();
      };

      if (modalType === 'ADD') {
        actionAddPlaceContact({ result: submittedValues, callBack });
      } else {
        actionEditPlaceContact({
          id: placeContactDetails._id,
          place: placeId,
          result: submittedValues,
          callBack,
        });
      }
    },
  });

  const onChangeOption = React.useCallback(
    (e) => {
      validation.setFieldValue(e.name, e.value, true);
    },
    [validation],
  );

  const CloseButton = React.useMemo(
    () => (
      <button
        className={styles.ButtonClose}
        onClick={onToggleModal}
        type="button"
      >
        <CloseButtonSvg />
      </button>
    ),
    [],
  );

  const onAddNewPhoneNumber = React.useCallback(() => {
    validation.setFieldValue('phoneNumbers', [
      ...validation.values.phoneNumbers,
      '',
    ]);
  }, [validation]);

  const onRemovePhoneNumber = React.useCallback(
    (index) => () => {
      const newPhoneNumbers = validation.values.phoneNumbers.filter(
        (_, i) => i !== index,
      );
      validation.setFieldValue('phoneNumbers', newPhoneNumbers);
    },
    [validation],
  );

  const onChangePhoneNumber = React.useCallback(
    (index) => (e) => {
      const newPhoneNumbers = [...validation.values.phoneNumbers];
      newPhoneNumbers[index] = e.target.value;
      validation.setFieldValue('phoneNumbers', newPhoneNumbers);
    },
    [validation],
  );

  return (
    <div className={styles.Component}>
      <Modal
        fade
        size="lg"
        isOpen={isOpen}
        toggle={onToggleModal}
        className={styles.Modal}
      >
        <form onSubmit={validation.handleSubmit}>
          <Loading loading={isLoadingGetPlaceContactDetails} loaderOnPage />

          <div className={styles.ModalHeaderWrapper}>
            <Label className={styles.ModalHeaderWrapperLabel}>
              {modalType === 'ADD' ? 'Thêm' : 'Chỉnh sửa'}
              {' '}
              thông tin liên hệ
            </Label>

            <ModalHeader
              toggle={onToggleModal}
              className={styles.ModalHeader}
              close={CloseButton}
            />
          </div>

          <ModalBody className={styles.ModalBody}>
            <Row className="Block">
              <Col md="5" className="Block__Title">
                Tên người liên hệ
                <code>*</code>
              </Col>

              <Col md="12" className="Block__Content__Wrapper">
                <AppInputValidation
                  inputClassName={styles.Input}
                  name="name"
                  id="name"
                  type="text"
                  placeholder="Nhập tên người liên hệ"
                  validation={validation}
                  // isShowCounter
                  limitCharacter={50}
                />
              </Col>
            </Row>

            <Row className="Block">
              <Col md="5" className="Block__Title">
                Giới tính
              </Col>

              <Col md="12" className="Block__Content__Wrapper">
                <div
                  className="GenderRadioGroup"
                  role="radiogroup"
                  aria-label="Giới tính"
                >
                  {gender.map(({ value, label }) => (
                    <label
                      key={value}
                      htmlFor={`modal-contact-gender-${value}`}
                      className="GenderRadio"
                    >
                      <Input
                        id={`modal-contact-gender-${value}`}
                        type="radio"
                        name="gender"
                        value={value}
                        checked={validation.values.gender === value}
                        onChange={() =>
                          validation.setFieldValue('gender', value, true)
                        }
                        onBlur={validation.handleBlur}
                      />

                      <span className="GenderRadio__Label">{label}</span>
                    </label>
                  ))}
                </div>
              </Col>
            </Row>

            <Row className="Block">
              <Col md="5" className="Block__Title">
                Ngôn ngữ ưu tiên
                <code>*</code>
              </Col>

              <Col md="12" className="Block__Content__Wrapper">
                <AppSelectValidation
                  placeholder="Vui lòng chọn ngôn ngữ"
                  name="mainLanguage"
                  key="mainLanguage"
                  options={languageOptions}
                  validation={validation}
                  value={validation.values.mainLanguage}
                  onChange={onChangeOption}
                />
              </Col>
            </Row>

            <Row className="Block">
              <Col md="5" className="Block__Title">
                Chức vụ
                <code>*</code>
              </Col>

              <Col md="12" className="Block__Content__Wrapper">
                <AppSelectValidation
                  placeholder="Vui lòng chọn chức vụ"
                  name="role"
                  key="role"
                  options={roleOptions}
                  validation={validation}
                  value={validation.values.role}
                  onChange={onChangeOption}
                />
              </Col>
            </Row>

            <Row className="Block">
              <Col md="5" className="Block__Title">
                Địa chỉ Email
                <code>*</code>
              </Col>

              <Col md="12" className="Block__Content__Wrapper">
                <AppInputValidation
                  name="email"
                  id="email"
                  type="text"
                  placeholder="Nhập địa chỉ email"
                  validation={validation}
                  inputClassName={styles.Input}
                />
              </Col>
            </Row>

            <Row className="Block BlockPhone">
              <Col md="5" className="Block__Title">
                Số điện thoại
                <code>*</code>
              </Col>

              <Col
                md="12"
                className="Block__Content__Wrapper BlockPhone__Content__Wrapper"
              >
                <div className="InputPhoneNumbersWrapper">
                  {validation.values.phoneNumbers.map((item, index) => (
                    <div className="position-relative" key={index}>
                      <AppInputValidation
                        inputClassName={`InputPhoneNumber ${styles.Input}`}
                        name={`phoneNumbers[${index}]`}
                        id={`phoneNumber-${index}`}
                        type="text"
                        placeholder="Số điện thoại"
                        value={item}
                        isCustomOnChange
                        onChange={onChangePhoneNumber(index)}
                        validation={validation}
                      />

                      {validation.values.phoneNumbers.length > 1 && (
                        <div
                          className={`BtnRemovePhoneNumber ${
                            validation.errors.phoneNumbers &&
                            validation.errors.phoneNumbers[index] &&
                            'hasError'
                          }`}
                          onClick={onRemovePhoneNumber(index)}
                          role="presentation"
                        >
                          x
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <AppButton
                  color="secondary"
                  classes="BtnAddRepresentative"
                  onClick={onAddNewPhoneNumber}
                >
                  <Add />

                  <p className="TextButton">Thêm số điện thoại liên hệ</p>
                </AppButton>
              </Col>
            </Row>

            <div className="Button__Wrapper">
              {modalType === 'EDIT' && (
                <AppButton
                  classes="Button__Danger"
                  onClick={onOpenModalDeletePlaceContact}
                >
                  Xóa
                </AppButton>
              )}

              <AppButton classes="Button Button__Cancel" onClick={onCloseModal}>
                Hủy
              </AppButton>

              <AppButton classes="Button Button__Save" type="submit">
                {modalType === 'ADD' ? 'Xác nhận' : 'Lưu'}
              </AppButton>
            </div>
          </ModalBody>
        </form>
      </Modal>
    </div>
  );
}

ModalAddContactInfo.propTypes = {
  modalType: PropTypes.string.isRequired,

  placeId: PropTypes.string.isRequired,

  isOpen: PropTypes.bool.isRequired,

  isLoadingGetPlaceContactDetails: PropTypes.bool.isRequired,
  placeContactDetails: PropTypes.instanceOf(Object),

  contactGroup: PropTypes.string.isRequired,

  languageSettings: PropTypes.instanceOf(Array),
  roleSettings: PropTypes.instanceOf(Array),

  onToggleModal: PropTypes.func.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onOpenModalDeletePlaceContact: PropTypes.func.isRequired,
  onAddSuccess: PropTypes.func,

  actionAddPlaceContact: PropTypes.func.isRequired,
  actionEditPlaceContact: PropTypes.func.isRequired,
  actionGetPlaceContacts: PropTypes.func.isRequired,
};

ModalAddContactInfo.defaultProps = {
  languageSettings: [],
  roleSettings: [],

  placeContactDetails: {},
  onAddSuccess: () => {},
};

export default memo(ModalAddContactInfo);
